import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const STORAGE_KEY = 'cookiePrefs';

const DEFAULT_PREFS = {
  necessary: true,   // locked, always true
  preferences: true,
  analytics: false,
  media: false,
};

export default function CookieSettings({t}) {
  const [prefs, setPrefs] = useState(DEFAULT_PREFS);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState('');

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        setPrefs({ ...DEFAULT_PREFS, ...parsed, necessary: true });
      }
    } catch {}
  }, []);

  function setKey(key, val) {
    setPrefs(p => ({ ...p, [key]: key === 'necessary' ? true : !!val }));
  }

  function acceptAll() {
    const allOn = { necessary: true, preferences: true, analytics: true, media: true };
    setPrefs(allOn);
    save(allOn, t('cookieSettings.toast.saved'));
  }

  function resetDefaults() {
    setPrefs(DEFAULT_PREFS);
    save(DEFAULT_PREFS, t('cookieSettings.toast.reset'));
  }

  function save(next = prefs, message = t('cookieSettings.toast.saved')) {
    setSaving(true);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      // Let the app react (e.g., load/unload scripts)
      window.dispatchEvent(new CustomEvent('cookie:prefs-changed', { detail: next }));
      setToast(message);
      setTimeout(() => setToast(''), 2500);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="max-w-3xl mx-auto bg-white border rounded shadow p-6 md:p-8 mt-6">
      <h1 className="text-3xl font-bold mb-2">{t('cookieSettings.title')}</h1>
      <p className="text-sm text-gray-500 mb-6">
        {t('cookieSettings.updated', { date: '2025-10-08' })}
      </p>
      <p className="mb-6">{t('cookieSettings.intro')}</p>

      <div className="divide-y border rounded">
        {/* Necessary (locked) */}
        <PrefRow
          title={t('cookieSettings.categories.necessary.title')}
          desc={t('cookieSettings.categories.necessary.desc')}
          checked={true}
          disabled
        />
        {/* Preferences */}
        <PrefRow
          title={t('cookieSettings.categories.preferences.title')}
          desc={t('cookieSettings.categories.preferences.desc')}
          checked={!!prefs.preferences}
          onChange={v => setKey('preferences', v)}
        />
        {/* Analytics */}
        <PrefRow
          title={t('cookieSettings.categories.analytics.title')}
          desc={t('cookieSettings.categories.analytics.desc')}
          checked={!!prefs.analytics}
          onChange={v => setKey('analytics', v)}
        />
        {/* Media */}
        <PrefRow
          title={t('cookieSettings.categories.media.title')}
          desc={t('cookieSettings.categories.media.desc')}
          checked={!!prefs.media}
          onChange={v => setKey('media', v)}
        />
      </div>

      <div className="flex flex-wrap gap-2 mt-6">
        <button
          className="px-4 py-2 rounded bg-neutral-100 hover:bg-neutral-200"
          onClick={resetDefaults}
        >
          {t('cookieSettings.actions.reset')}
        </button>
        <button
          className="px-4 py-2 rounded bg-blue-600 text-white hover:opacity-90 disabled:opacity-60"
          onClick={() => save()}
          disabled={saving}
        >
          {saving ? t('cookieSettings.actions.saving') : t('cookieSettings.actions.save')}
        </button>
        <button
          className="ml-auto px-4 py-2 rounded bg-emerald-600 text-white hover:opacity-90"
          onClick={acceptAll}
        >
          {t('cookieSettings.actions.acceptAll')}
        </button>
      </div>

      {toast && (
        <div className="mt-4 rounded bg-emerald-50 text-emerald-800 border border-emerald-200 px-3 py-2 text-sm">
          {toast}
        </div>
      )}
    </div>
  );
}

function PrefRow({ title, desc, checked, onChange, disabled }) {
  return (
    <label className="flex items-start gap-4 p-4">
      <input
        type="checkbox"
        className="mt-1 h-4 w-4"
        checked={!!checked}
        disabled={disabled}
        onChange={(e) => onChange?.(e.target.checked)}
      />
      <div>
        <div className="font-medium">{title}</div>
        <div className="text-sm text-gray-600">{desc}</div>
      </div>
    </label>
  );
}
